import React, { createContext, lazy, useContext, useState, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  ProgressOverlayStatus,
  useKeyboardShortcuts,
  useMenuOverlay,
  useOverlay,
  useProgressOverlay,
} from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { css, Global } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import { splunk } from '@fs/zion-splunk-logger'

import { filenameFriendlyDate, keepOutput, KEEP_PROCESS_TYPE, supportsHDCanvas } from '../../helpers'
import DownloadQuickGlance from './DownloadQuickGlance' // Not lazy imported to avoid a flash of no content
import PrintDialog from './PrintDialog' // Not lazy imported to avoid a flash of no content

const { DOWNLOAD, PRINT } = KEEP_PROCESS_TYPE
const { FAILED, IN_PROGRESS, SUCCESSFUL } = ProgressOverlayStatus

const CancelConfirmationDialog = lazy(
  () => import('../../components/CancelConfirmationDialog/CancelConfirmationDialog')
)

const KeepContext = createContext()

export function KeepProvider({ children, showAvatars = false, showPortraits = false }) {
  const downloadRef = useRef()
  const elementRef = useRef()
  const filename = useRef(`${i18n.t('useKeep.KeepProvider.filename', 'Keepsake')}-${filenameFriendlyDate()}`)
  const fileType = useRef('pdf')
  const hidePrintDialog = useRef(false)
  const imageScale = useRef(2)
  const imageSize = useRef('a4Square')
  const imageQuality = useRef(1)
  const isCanceled = useRef(false)
  // keepStyles is an object of callback functions.
  // Using a key ensures that we don't duplicate callback functions.
  // It also allows for easy removal if needed.
  const keepStyles = useRef({})
  const pageSelector = useRef(':scope > *')
  const pageOrientation = useRef('portrait')
  const pageSize = useRef('a4')
  const printStrategy = useRef() // browser || auto
  const dpi = useRef(600)
  const fontElements = useRef([])
  const fonts = useRef([])
  const maxCanvasSize = useRef()
  const shouldCancel = useRef()
  const shouldCenter = useRef(false)
  const togglePrintStrategy = useRef(false)
  const totalPages = useRef(1)
  const [isMultiPage, setIsMultiPage] = useState(false)
  const [processing, setProcessing] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const [downloadDesignName, setDownloadDesignName] = useState('')
  const cancelOverlay = useOverlay()
  const downloadOverlay = useMenuOverlay()
  const printOverlay = useOverlay()
  const { createProgressItem, setHeaderMessage, setOnDismiss } = useProgressOverlay()
  const iosSafariDownload = useFeatureFlag('chinese_treeDesigns_iosSafariDownload')

  const handleDownloadClick = useCallback(
    (event, designName) => {
      // This adjusts the context so the download overlay shows in the correct position and not at the bottom of the screen.
      downloadOverlay.handleClick.call(downloadOverlay, event)
      setDownloadDesignName(designName)
    },
    [downloadOverlay, setDownloadDesignName]
  )

  const dynamicallySetPrintStrategy = useCallback(async () => {
    const doesSupportHDCanvas = await supportsHDCanvas()
    if (doesSupportHDCanvas) {
      printStrategy.current = 'auto'
    } else {
      printStrategy.current = 'browser'
    }
  }, [printStrategy])

  const handleProcess = useCallback(
    async ({ process, focusBackRef, downloadType, designName: printDesignName, fromMemoryBooks }) => {
      if (!isReady || processing) {
        return null
      }

      downloadOverlay.close({ target: focusBackRef?.current })

      let onAfterPrintRan = false
      function onAfterPrint() {
        printOverlay.open({ target: focusBackRef?.current })
        onAfterPrintRan = true
      }

      fileType.current = 'pdf'
      if (process === DOWNLOAD) {
        fileType.current = downloadType
      } else if (
        (printStrategy.current === 'browser' && !togglePrintStrategy.current) ||
        (printStrategy.current !== 'browser' && togglePrintStrategy.current)
      ) {
        window.addEventListener('afterprint', onAfterPrint, { once: true })
        window.print()
        if (!onAfterPrintRan) {
          onAfterPrint()
        }
        return null
      }

      isCanceled.current = false
      setProcessing({ fileType: fileType.current, process, fromMemoryBooks })

      let onProgress = null
      if (!fromMemoryBooks) {
        // `set` must come before `create`
        setHeaderMessage(i18n.t('shared.useProgressOverlay.header', 'Tree Designs'))
        setOnDismiss(() => {
          isCanceled.current = true
          setProcessing(null)
        })
        const progressOverlayItemUpdate = createProgressItem({}, async () => {
          cancelOverlay.open()

          // Wait for the cancel confirmation dialog to be dismissed.
          // shouldCancel.current contains the response of the dialog.
          const didCancel = await new Promise((resolve) => {
            shouldCancel.current = resolve
          })

          cancelOverlay.close({ target: focusBackRef?.current })

          if (didCancel) {
            isCanceled.current = true
            setProcessing(null)
          }

          return didCancel
        })

        const processProgressOverlayItemUpdate = ({ message, percent, status }) => {
          let icon = '📥' // Download
          if (process === PRINT) {
            icon = '🖨️'
          }

          message = `${icon} ${message}`
          progressOverlayItemUpdate({ message, percent, status })
        }

        processProgressOverlayItemUpdate({
          message: i18n.t('useKeep.onProgress.started', 'Started'),
          status: IN_PROGRESS,
        })

        // Allow the progress to be animated in before starting the process starts.
        await new Promise((resolve) => {
          setTimeout(resolve, 800)
        })
        onProgress = ({ status, ...rest }) => {
          processProgressOverlayItemUpdate({ status, ...rest })

          const logDesignAction = (action, designName) => {
            try {
              const fileTypeText = fileType ? ` ${fileType.current}` : ''
              // SPLUNK_REVIEW[DATABRICKS]: Business/analytics event emitted to Splunk via SDK.
              splunk.log(`${action} tree design: ${designName}${fileTypeText}`)
            } catch (error) {
              console.error('Error logging to Splunk:', error)
            }
          }

          if (status === SUCCESSFUL || status === FAILED) {
            setProcessing(null)
            cancelOverlay.close({ target: focusBackRef?.current })

            if (status === SUCCESSFUL) {
              switch (process) {
                case DOWNLOAD:
                  if (fromMemoryBooks) {
                    logDesignAction('For Memory Books', printDesignName)
                  } else {
                    logDesignAction('Download', downloadDesignName)
                  }
                  break
                case PRINT:
                  logDesignAction('Print', printDesignName)
                  break
                default:
                  break
              }
            }
          }

          if (!hidePrintDialog && status === SUCCESSFUL && process === PRINT) {
            printOverlay.open({ target: focusBackRef?.current })
          }
        }
      }

      return keepOutput({
        cancelOverlay,
        dpi,
        elementRef,
        fileType,
        filename,
        fontElements,
        fonts,
        fromMemoryBooks,
        imageQuality,
        imageScale,
        imageSize,
        iosSafariDownload,
        isCanceled,
        keepStyles,
        maxCanvasSize: maxCanvasSize.current,
        onProgress,
        pageSelector,
        pageOrientation,
        pageSize,
        process,
        shouldCenter,
        totalPages,
      })
    },
    [
      cancelOverlay,
      createProgressItem,
      downloadDesignName,
      downloadOverlay,
      iosSafariDownload,
      isReady,
      printOverlay,
      processing,
      setHeaderMessage,
      setOnDismiss,
    ]
  )

  // Ctrl/Cmd + D to download
  // useKeyboardShortcuts doesn't prevent default, so we need to do it manually.
  window.addEventListener('keydown', (event) => {
    if ((event.ctrlKey || event.metaKey) && ['d', 'p'].includes(event.key)) {
      event.preventDefault()
    }
  })
  useKeyboardShortcuts({
    handlers: {
      KeyD: {
        ctrl: {
          action: () => handleProcess({ process: DOWNLOAD }),
          title: i18n.t('common-ui:download.action', 'Download'),
        },
      },
      KeyP: {
        ctrl: {
          action: () => handleProcess({ process: PRINT }),
          title: i18n.t('common-ui:print.action', 'Print'),
        },
      },
    },
    enabled: true,
  })

  const value = useMemo(
    () => ({
      dpi,
      downloadOverlay,
      downloadRef,
      dynamicallySetPrintStrategy,
      elementRef,
      fileType,
      filename,
      fontElements,
      fonts,
      handleDownloadClick,
      handleProcess,
      hidePrintDialog,
      imageQuality,
      imageScale,
      imageSize,
      isReady,
      keepStyles,
      pageOrientation,
      pageSelector,
      pageSize,
      printStrategy,
      processing,
      setIsMultiPage,
      setIsReady,
      setProcessing,
      shouldCenter,
      totalPages,
    }),
    [downloadOverlay, dynamicallySetPrintStrategy, handleProcess, processing, isReady, handleDownloadClick]
  )

  return (
    <KeepContext.Provider value={value}>
      <Global
        styles={css`
          div[status] > p > span > span[multiline].ellipsisCss_e139x152 {
            display: block !important;
            padding-inline-start: 3em;

            &::first-letter {
              float: inline-start;
              margin-inline-start: -2.3em;
            }
          }
        `}
      />
      {children}
      {(cancelOverlay.isOpen || cancelOverlay.transitioning) && (
        <CancelConfirmationDialog
          cancelOverlay={cancelOverlay}
          process={i18n.t('common-ui:download.action', 'Download')}
          shouldCancel={shouldCancel}
        />
      )}
      {(downloadOverlay.isOpen || downloadOverlay.transitioning) && (
        <DownloadQuickGlance
          isMultiPage={isMultiPage}
          downloadOverlay={downloadOverlay}
          downloadRef={downloadRef}
          handleProcess={handleProcess}
          showAvatars={showAvatars}
          showPortraits={showPortraits}
        />
      )}
      {!hidePrintDialog.current && (printOverlay.isOpen || printOverlay.transitioning) && (
        <PrintDialog printOverlay={printOverlay} />
      )}
    </KeepContext.Provider>
  )
}

KeepProvider.propTypes = {
  children: PropTypes.node.isRequired,
  showAvatars: PropTypes.bool,
  showPortraits: PropTypes.bool,
}

export default function useKeep() {
  return useContext(KeepContext)
}
